import { Address, ResolvedConfig } from "abitype";
import { getPublicClient } from "@wagmi/core";

import { erc20ABI, readContracts } from "wagmi";
import { ContractFunctionExecutionError, formatUnits, hexToString, isAddress } from "viem";

export const erc20ABI_bytes32 = [
  {
    type: "event",
    name: "Approval",
    inputs: [
      {
        indexed: true,
        name: "owner",
        type: "address",
      },
      {
        indexed: true,
        name: "spender",
        type: "address",
      },
      {
        indexed: false,
        name: "value",
        type: "uint256",
      },
    ],
  },
  {
    type: "event",
    name: "Transfer",
    inputs: [
      {
        indexed: true,
        name: "from",
        type: "address",
      },
      {
        indexed: true,
        name: "to",
        type: "address",
      },
      {
        indexed: false,
        name: "value",
        type: "uint256",
      },
    ],
  },
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      {
        name: "owner",
        type: "address",
      },
      {
        name: "spender",
        type: "address",
      },
    ],
    outputs: [
      {
        name: "",
        type: "uint256",
      },
    ],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "spender",
        type: "address",
      },
      {
        name: "amount",
        type: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "bool",
      },
    ],
  },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [
      {
        name: "account",
        type: "address",
      },
    ],
    outputs: [
      {
        name: "",
        type: "uint256",
      },
    ],
  },
  {
    type: "function",
    name: "decimals",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "uint8",
      },
    ],
  },
  {
    type: "function",
    name: "name",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "bytes32",
      },
    ],
  },
  {
    type: "function",
    name: "symbol",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "bytes32",
      },
    ],
  },
  {
    type: "function",
    name: "totalSupply",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "uint256",
      },
    ],
  },
  {
    type: "function",
    name: "transfer",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "recipient",
        type: "address",
      },
      {
        name: "amount",
        type: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "bool",
      },
    ],
  },
  {
    type: "function",
    name: "transferFrom",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "sender",
        type: "address",
      },
      {
        name: "recipient",
        type: "address",
      },
      {
        name: "amount",
        type: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "bool",
      },
    ],
  },
] as const;

export type FetchAllowanceArgs = {
  /** Owner Address or Owner ENS name */
  ownerAddressOrName: string;
  /** Spender Address or Spender ENS name */
  spenderAddressOrName: string;
  /** Chain id to use for provider */
  chainId?: number;
  /** Units for formatting output */
  formatUnits?: number;
  /** ERC-20 address */
  token: Address;
};

export type FetchAllowanceResult = {
  decimals: ResolvedConfig["IntType"];
  formatted: string;
  symbol: string;
  value: ResolvedConfig["BigIntType"];
};

export async function fetchAllowance({
  ownerAddressOrName,
  spenderAddressOrName,
  chainId,
  formatUnits: unit,
  token,
}: FetchAllowanceArgs): Promise<FetchAllowanceResult> {
  const provider = getPublicClient({ chainId });

  // Convert ENS name to address if required
  let resolvedOwnerAddress: Address;
  if (isAddress(ownerAddressOrName)) resolvedOwnerAddress = ownerAddressOrName as Address;
  else {
    // TODO: use https://viem.sh/docs/ens/actions/getEnsResolver.html
    // const address = (await provider.resolveName(ownerAddressOrName)) as Address | null;
    // // Same error `provider.getAllowance` throws for invalid ENS name
    // if (!address)
    //   logger.throwError("ENS name not configured", Logger.errors.UNSUPPORTED_OPERATION, {
    //     operation: `resolveName(${JSON.stringify(ownerAddressOrName)})`,
    //   });
    // resolvedOwnerAddress = address;
  }

  let resolvedSpenderAddress: Address;
  if (isAddress(spenderAddressOrName)) resolvedSpenderAddress = spenderAddressOrName as Address;
  else {
    // TODO: use https://viem.sh/docs/ens/actions/getEnsResolver.html
    // const address = (await provider.resolveName(spenderAddressOrName)) as Address | null;
    // // Same error `provider.getAllowance` throws for invalid ENS name
    // if (!address)
    //   logger.throwError("ENS name not configured", Logger.errors.UNSUPPORTED_OPERATION, {
    //     operation: `resolveName(${JSON.stringify(spenderAddressOrName)})`,
    //   });
    // resolvedSpenderAddress = address;
  }

  type FetchContractAllowance = {
    abi: typeof erc20ABI | typeof erc20ABI_bytes32;
  };
  const fetchContractAllowance = async ({ abi }: FetchContractAllowance) => {
    const erc20Config = { abi, address: token, chainId } as const;
    const [value, decimals, symbol] = await readContracts({
      allowFailure: false,
      contracts: [
        {
          ...erc20Config,
          functionName: "allowance",
          args: [resolvedOwnerAddress, resolvedSpenderAddress],
        },
        { ...erc20Config, functionName: "decimals" },
        { ...erc20Config, functionName: "symbol" },
      ],
    });
    return {
      decimals,
      formatted: formatUnits(value ?? "0", unit ?? decimals),
      symbol: symbol as string, // protect against `ResolvedConfig['BytesType']`
      value,
    };
  };

  try {
    return await fetchContractAllowance({ abi: erc20ABI });
  } catch (err) {
    // In the chance that there is an error upon decoding the contract result,
    // it could be likely that the contract data is represented as bytes32 instead
    // of a string.
    if (err instanceof ContractFunctionExecutionError) {
      const { symbol, ...rest } = await fetchContractAllowance({
        abi: erc20ABI_bytes32,
      });
      return {
        symbol: hexToString(symbol as Address),
        ...rest,
      };
    }
    throw err;
  }
}
